import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { hostelService } from '../services/api';
import { Hostel, Invoice, User } from '../types';
import { GuestStayContext } from '../utils/guestInvoiceDisplay';
import { FloorRoomLookup } from '../utils/locationLabels';
import { InvoicePdfDocument, invoicePdfNumber } from './InvoicePdfDocument';
import './InvoiceViewModal.css';

function hostelNeedsFullDetails(h: Hostel | null | undefined): boolean {
  if (!h?.id) return true;
  return !h.address?.trim() && !h.phone?.trim() && !h.email?.trim();
}

export type InvoiceViewModalProps = {
  invoice: Invoice;
  tenant?: User | null;
  hostel?: Hostel | null;
  locationLookup?: FloorRoomLookup;
  /** Current daily-guest stay — used when invoice note JSON is missing rate/days. */
  guestStay?: GuestStayContext | null;
  onClose: () => void;
  onDownload: (invoice: Invoice) => void;
};

export const InvoiceViewModal: React.FC<InvoiceViewModalProps> = ({
  invoice,
  tenant,
  hostel,
  locationLookup,
  guestStay,
  onClose,
  onDownload,
}) => {
  const [displayHostel, setDisplayHostel] = useState<Hostel | null>(hostel ?? null);

  useEffect(() => {
    const hid = invoice.hostelId ?? hostel?.id;
    if (!hid) {
      setDisplayHostel(hostel ?? null);
      return;
    }
    if (hostel && !hostelNeedsFullDetails(hostel)) {
      setDisplayHostel(hostel);
      return;
    }
    let cancelled = false;
    void hostelService
      .getHostelById(hid)
      .then((full) => {
        if (!cancelled) setDisplayHostel(full);
      })
      .catch(() => {
        if (!cancelled) setDisplayHostel(hostel ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [invoice.hostelId, hostel]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.classList.add('inv-view-modal-open');
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('inv-view-modal-open');
    };
  }, [onClose]);

  const handlePrint = () => {
    window.print();
  };

  const modal = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="inv-view-overlay" role="presentation" onClick={onClose}>
      <div
        className="inv-view-shell"
        role="dialog"
        aria-labelledby="inv-view-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inv-view-toolbar no-print">
          <h2 id="inv-view-title">Invoice {invoicePdfNumber(invoice.id)}</h2>
          <div className="inv-view-toolbar-actions">
            <button type="button" className="inv-view-btn inv-view-btn--ghost" onClick={handlePrint}>
              Print
            </button>
            <button type="button" className="inv-view-btn inv-view-btn--primary" onClick={() => onDownload(invoice)}>
              Download PDF
            </button>
            <button type="button" className="inv-view-close" aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        <div className="inv-view-canvas">
          <InvoicePdfDocument
            invoice={invoice}
            tenant={tenant}
            hostel={displayHostel}
            locationLookup={locationLookup}
            guestStay={guestStay}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
